import React, { useEffect, useState } from "react";
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Button,
  TextField,
  Menu,
  MenuItem,
} from "@mui/material";
import { useTheme, useMediaQuery } from '@mui/material';
import DatePickerField from "../../common/DatePickerDashboard";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { makeStyles } from "@mui/styles";
import { useDispatch, useSelector } from "react-redux";
import { searchBotAllReportAction, searchBotDownloadAllReportAction } from "../../redux/actions/botAction";
import html2canvas from 'html2canvas';
import jsPDF from "jspdf";
import autoTable from 'jspdf-autotable';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import Loader from "../loader/loader";
import Pagination from "@mui/material/Pagination";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import NoDataFound from "../../common/NoDataFound";
import MoreVertIcon from '@mui/icons-material/MoreVert';

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: 20,
    padding: 16,
    backgroundColor: "#f5f5f5",
    borderRadius: 8,
    boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.1)",
  },
  table: {
    minWidth: 650,
  },
  tableCell: {
    fontWeight: "bold",
    backgroundColor: "#b0b0b0",
    color: "white",
    textAlign: "center",
  },
  tableRow: {
    "&:hover": {
      backgroundColor: "#f1f1f1",
    },
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
  },
  tableContainer: {
    borderRadius: 8,
    boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.1)",
  },
  iconButton: {
    marginRight: 8,
    "&:hover": {
      backgroundColor: "#e0e0e0",
    },
  },
  downloadButton: {
    backgroundColor: "#4CAF50",
    color: "white",
    "&:hover": {
      backgroundColor: "#45a049",
    },
  },
  tableCellHeader: {
    fontWeight: '700 !important',
    backgroundColor: '#1565c0 !important',
    color: '#ffffff !important',
    fontFamily: '"Poppins", sans-serif !important',
    textAlign: "center !important",
    padding: '10px !important',
  },
  tableCellData: {
    textAlign: "center",
  },
  styledTableRow: {
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
    },
    '&:hover': {
      backgroundColor: theme.palette.action.selected,
    },
    '& td, & th': {
      padding: theme.spacing(1.5),
      fontFamily: '"Poppins", sans-serif',
    }
  },
  // Fixed height container for date fields
  dateFieldContainer: {
    height: '85px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
  },
}));

function BotAllReport() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const searchBot = useSelector((state) => state.bot.searchBot);
  const searchBotDownload = useSelector((state) => state.bot.searchBotDownload);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  
  
  const [downloadAnchorEl, setDownloadAnchorEl] = useState(null);
  const handleDownloadClick = (event) => setDownloadAnchorEl(event.currentTarget);
  const handleDownloadClose = () => setDownloadAnchorEl(null);
  
  
  const [fromDateInput, setFromDateInput] = useState(null);
  const [toDateInput, setToDateInput] = useState(null);
  
  const [botData, setBotData] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const searchreport = searchBot.content || [];
  const count = hasSearched ? searchBot.totalElements : 0;
  const searchReportDownload = searchBotDownload.content || [];

  const defaultFromDate = null;
  const defaultToDate = null;

  const [formData, setFormData] = useState({
    fromDate: '',
    toDate: ''
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});

  // Fixed validation functions
  const validateFromDate = (value) => {
    if (!value || value.trim() === '') {
      return "From date is required";
    }
    
    const fromDate = dayjs(value, 'MM-DD-YYYY');
    if (!fromDate.isValid()) {
      return "Invalid from date format";
    }
    
    if (formData.toDate && formData.toDate.trim() !== '') {
      const toDate = dayjs(formData.toDate, 'MM-DD-YYYY');
      if (fromDate.isAfter(toDate)) {
        return "From date cannot be after to date";
      }
    }
    
    return null;
  };

  const validateToDate = (value) => {
    if (!value || value.trim() === '') {
      return "To date is required";
    }
    
    const toDate = dayjs(value, 'MM-DD-YYYY');
    if (!toDate.isValid()) {
      return "Invalid to date format";
    }
    
    if (formData.fromDate && formData.fromDate.trim() !== '') {
      const fromDate = dayjs(formData.fromDate, 'MM-DD-YYYY');
      if (toDate.isBefore(fromDate)) {
        return "To date cannot be before from date";
      }
    }
    
    const today = dayjs();
    if (toDate.isAfter(today)) {
      return "To date cannot be in the future";
    }
    
    return null;
  };

  const validateDateRange = (fromDateValue, toDateValue) => {
    if (fromDateValue && toDateValue && fromDateValue.trim() !== '' && toDateValue.trim() !== '') {
      const fromDate = dayjs(fromDateValue, 'MM-DD-YYYY');
      const toDate = dayjs(toDateValue, 'MM-DD-YYYY');
      const daysDifference = toDate.diff(fromDate, 'day');
      
     
    }
    return null;
  };

  // Updated handleDateChange with proper validation timing
  const handleDateChange = (fieldName, value) => {
    const updatedFormData = { ...formData, [fieldName]: value };
    setFormData(updatedFormData);
    
    if (fieldName === 'fromDate') {
      setFromDateInput(value ? dayjs(value, 'MM-DD-YYYY') : null);
    } else if (fieldName === 'toDate') {
      setToDateInput(value ? dayjs(value, 'MM-DD-YYYY') : null);
    }

    // Clear previous errors immediately
    setFieldErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      delete newErrors.dateRange;
      delete newErrors.general;
      return newErrors;
    });

    // Validate immediately with updated data
    setTimeout(() => {
      const newErrors = {};
      
      if (fieldName === 'fromDate') {
        const fromDateError = validateFromDate(value);
        if (fromDateError && value && value.trim() !== '') {
          newErrors.fromDate = fromDateError;
        }
      } else if (fieldName === 'toDate') {
        const toDateError = validateToDate(value);
        if (toDateError && value && value.trim() !== '') {
          newErrors.toDate = toDateError;
        }
      }

      // Validate range only if both dates are filled
      const fromDateForRange = fieldName === 'fromDate' ? value : formData.fromDate;
      const toDateForRange = fieldName === 'toDate' ? value : formData.toDate;
      
      if (fromDateForRange && toDateForRange && fromDateForRange.trim() !== '' && toDateForRange.trim() !== '') {
        const rangeError = validateDateRange(fromDateForRange, toDateForRange);
        if (rangeError) {
          newErrors.dateRange = rangeError;
        }
      }

      if (Object.keys(newErrors).length > 0) {
        setFieldErrors(prev => ({ ...prev, ...newErrors }));
      }
    }, 100);
  };

  const handleBlur = (fieldName, errorMsg) => {
    setTouchedFields((prev) => ({ ...prev, [fieldName]: true }));
    
    // Only validate on blur if field has a value
    const fieldValue = formData[fieldName];
    if (!fieldValue || fieldValue.trim() === '') {
      return; // Don't show validation errors for empty fields on blur
    }
    
    let error = null;
    if (fieldName === 'fromDate') {
      error = validateFromDate(formData.fromDate);
    } else if (fieldName === 'toDate') {
      error = validateToDate(formData.toDate);
    }
    
    if (error) {
      setFieldErrors((prev) => ({ ...prev, [fieldName]: error }));
    }
  };

  const handleSort = (field) => {
    const isAsc = sortField === field && sortOrder === "asc";
    setSortField(field);
    setSortOrder(isAsc ? "desc" : "asc");
  };

  useEffect(() => {
    if (count > 0) {
      dispatch(
        searchBotDownloadAllReportAction(
          {
            fromDate: formData.fromDate,
            toDate: formData.toDate,
            search,
          },
          0,
          count
        )
      );
    }
  }, [dispatch, page, rowsPerPage, count, formData.fromDate, formData.toDate, search]);

  useEffect(() => {
    if (hasSearched) {
      setBotData(getBotData(searchreport));
    }
  }, [searchBot, hasSearched, searchreport]);

  const validateForm = () => {
    const errors = {};
    
    // Only validate if we're trying to search with dates
    if (formData.fromDate || formData.toDate) {
      const fromDateError = validateFromDate(formData.fromDate);
      if (fromDateError) errors.fromDate = fromDateError;
      
      const toDateError = validateToDate(formData.toDate);
      if (toDateError) errors.toDate = toDateError;
      
      const rangeError = validateDateRange(formData.fromDate, formData.toDate);
      if (rangeError) errors.dateRange = rangeError;
    }
    
    return errors;
  };

 const handleSearch = async () => {
  // ✅ Allow search if either date range OR keyword is provided
  if (
    !((formData.fromDate && formData.toDate) || search.trim())
  ) {
    setFieldErrors({
      general: "Please provide either a date range or a search term.",
    });
    setTouchedFields({
      fromDate: true,
      toDate: true,
    });
    return;
  }

  // ✅ Validate remaining fields (if needed)
  const errors = validateForm();
  if (Object.keys(errors).length > 0) {
    setFieldErrors(errors);
    setTouchedFields({
      fromDate: true,
      toDate: true,
    });
    return;
  }

  setIsLoading(true);
  setHasSearched(false);
  setPage(0);

  try {
    await dispatch(
      searchBotAllReportAction(
        {
          fromDate: formData.fromDate,
          toDate: formData.toDate,
          search,
        },
        0,
        rowsPerPage
      )
    );

    setHasSearched(true);
  } finally {
    setIsLoading(false);
  }
};


  const resetSearch = () => {
    setHasSearched(false);
    setSearch("");
    setBotData([]);
    
    setFromDateInput(null);
    setToDateInput(null);
    
    const resetFormData = {
      fromDate: defaultFromDate ? defaultFromDate.format('MM-DD-YYYY') : '',
      toDate: defaultToDate ? defaultToDate.format('MM-DD-YYYY') : '',
    };
    
    setFormData(resetFormData);
    setFieldErrors({});
    setTouchedFields({});
    setPage(0);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDownloadCSV = () => {
    const dataToExport = searchReportDownload.map(row => ({
      "Carrier Name": row.carrierName || '',
      "Start Date": row.startDate || '',
      "Status": row.status || '',
      "Purchased By": row.purchasedByUserEmail || '',
      "Agency Code": row.carrierAgencyCode || '',
      "Recent Updates": row.changes || '',
      "Create Time": row.insertedOn ? dayjs(row.insertedOn).format('MM-DD-YYYY, hh:mm:ss A') : ''
    }));

    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [
        Object.keys(dataToExport[0]).join(','), 
        ...dataToExport.map(row => Object.values(row).map(val => `"${val}"`).join(','))
      ].join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Bot_All_Report_${dayjs().format('YYYYMMDD_HHmmss')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    handleDownloadClose();
  };

  const handleDownloadExcel = () => {
    const dataToExport = searchReportDownload.map(row => ({
      "Carrier Name": row.carrierName || '',
      "Start Date": row.startDate || '',
      "Status": row.status || '',
      "Purchased By": row.purchasedByUserEmail || '',
      "Agency Code": row.carrierAgencyCode || '',
      "Recent Updates": row.changes || '',
      "Create Time": row.insertedOn ? dayjs(row.insertedOn).format('MM-DD-YYYY, hh:mm:ss A') : ''
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Bot All Report");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(data, `Bot_All_Report_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`);
    handleDownloadClose();
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF('landscape');
    const tableColumn = [
      "Carrier Name", "Start Date", "Status", "Purchased By", 
      "Agency Code", "Recent Updates", "Create Time"
    ];

    const tableRows = searchReportDownload.map(row => [
      row.carrierName || '',
      row.startDate || '',
      row.status || '',
      row.purchasedByUserEmail || '',
      row.carrierAgencyCode || '',
      row.changes || '',
      row.insertedOn ? dayjs(row.insertedOn).format('MM-DD-YYYY, hh:mm:ss A') : ''
    ]);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [246, 194, 30] },
      margin: { top: 20 },
      didDrawPage: function (data) {
        doc.setFontSize(16);
        doc.text('Bot All Report', 15, 15);
      }
    });

    doc.save(`Bot_All_Report_${dayjs().format('YYYYMMDD_HHmmss')}.pdf`);
    handleDownloadClose();
  };

  const downloadPDF = async () => {
    try {
      setIsDownloading(true);
      const allData = searchReportDownload;
      const pdf = new jsPDF('l', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margins = 15;

      const hiddenDiv = document.createElement('div');
      hiddenDiv.style.position = 'absolute';
      hiddenDiv.style.left = '-9999px';
      hiddenDiv.style.top = '-9999px';

      pdf.setFontSize(16);
      pdf.text('Bot All Report', margins, margins);
      pdf.setFontSize(12);

      const rowHeight = 12;
      const headerHeight = 25;
      const footerHeight = 10;
      const availableHeight = pageHeight - margins * 2 - headerHeight - footerHeight;
      const rowsPerPdfPage = Math.floor(availableHeight / rowHeight);

      const tableStyles = {
        fontSize: 10,
        font: 'Poppins',
        headerBgColor: '#EAC86B',
        headerTextColor: '#000',
        rowBgColor: '#FFF2CF',
        cellTextColor: '#000',
        borderColor: '#CECECE',
        cellPadding: 5,
        textAlign: 'center',
      };

      const createTableHeader = () => {
        return `
          <tr style="background-color: ${tableStyles.headerBgColor}; color: ${tableStyles.headerTextColor}; font-family: ${tableStyles.font}; font-weight: bold;">
            <th style="padding: ${tableStyles.cellPadding}px; border: 1px solid ${tableStyles.borderColor}; text-align: ${tableStyles.textAlign}">Carrier Name</th>
            <th style="padding: ${tableStyles.cellPadding}px; border: 1px solid ${tableStyles.borderColor}; text-align: ${tableStyles.textAlign}">Start Date</th>
            <th style="padding: ${tableStyles.cellPadding}px; border: 1px solid ${tableStyles.borderColor}; text-align: ${tableStyles.textAlign}">Status</th>
            <th style="padding: ${tableStyles.cellPadding}px; border: 1px solid ${tableStyles.borderColor}; text-align: ${tableStyles.textAlign}">Purchased By</th>
            <th style="padding: ${tableStyles.cellPadding}px; border: 1px solid ${tableStyles.borderColor}; text-align: ${tableStyles.textAlign}">Agency Code</th>
            <th style="padding: ${tableStyles.cellPadding}px; border: 1px solid ${tableStyles.borderColor}; text-align: ${tableStyles.textAlign}">Recent Updates</th>
            <th style="padding: ${tableStyles.cellPadding}px; border: 1px solid ${tableStyles.borderColor}; text-align: ${tableStyles.textAlign}">Create Time</th>
          </tr>
        `;
      };

      const createTableRow = (row, index) => {
        const rowBgColor = index % 2 === 0 ? tableStyles.rowBgColor : 'transparent';
        return `
          <tr style="background-color: ${rowBgColor}; font-family: ${tableStyles.font};">
            <td style="padding: ${tableStyles.cellPadding}px; border: 1px solid ${tableStyles.borderColor}; text-align: ${tableStyles.textAlign}">${row.carrierName || ''}</td>
            <td style="padding: ${tableStyles.cellPadding}px; border: 1px solid ${tableStyles.borderColor}; text-align: ${tableStyles.textAlign}">${row.startDate || ''}</td>
            <td style="padding: ${tableStyles.cellPadding}px; border: 1px solid ${tableStyles.borderColor}; text-align: ${tableStyles.textAlign}">${row.status || ''}</td>
            <td style="padding: ${tableStyles.cellPadding}px; border: 1px solid ${tableStyles.borderColor}; text-align: ${tableStyles.textAlign}">${row.purchasedByUserEmail || ''}</td>
            <td style="padding: ${tableStyles.cellPadding}px; border: 1px solid ${tableStyles.borderColor}; text-align: ${tableStyles.textAlign}">${row.carrierAgencyCode || ''}</td>
            <td style="padding: ${tableStyles.cellPadding}px; border: 1px solid ${tableStyles.borderColor}; text-align: ${tableStyles.textAlign}">${row.changes || ''}</td>
            <td style="padding: ${tableStyles.cellPadding}px; border: 1px solid ${tableStyles.borderColor}; text-align: ${tableStyles.textAlign}">${row.insertedOn ? dayjs(row.insertedOn).format('MM-DD-YYYY, hh:mm:ss A') : ''}</td>
          </tr>
        `;
      };

      for (let i = 0; i < allData.length; i += rowsPerPdfPage) {
        if (i > 0) pdf.addPage();

        const pageData = allData.slice(i, i + rowsPerPdfPage);
        const tableHtml = `
           <table style="width: 100%; border-collapse: collapse; font-size: ${tableStyles.fontSize}px;">
        ${createTableHeader()}
        ${pageData.map((row, index) => createTableRow(row, i + index)).join('')}
      </table>
      `;

        hiddenDiv.innerHTML = tableHtml;
        document.body.appendChild(hiddenDiv);

        const canvas = await html2canvas(hiddenDiv, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff'
        });

        document.body.removeChild(hiddenDiv);

        const imgData = canvas.toDataURL('image/png');
        const imgProps = pdf.getImageProperties(imgData);
        const pdfWidth = pageWidth - (margins * 2);
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

        pdf.addImage(imgData, 'PNG', margins, i === 0 ? margins + 15 : margins, pdfWidth, pdfHeight);

        const pageNumber = `Page ${pdf.internal.getNumberOfPages()}`;
        pdf.setFontSize(10);
        pdf.text(pageNumber, pageWidth - margins - 20, pageHeight - margins);
      }

      const date = new Date().toLocaleDateString();
      pdf.text(`Generated on: ${date}`, margins, pageHeight - margins);
      pdf.text(`Total Records: ${allData.length}`, margins, pageHeight - margins - 5);

      pdf.save(`bot-all-report-${date}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{
        padding: isMobile ? '8px' : '24px',
        backgroundColor: '#ffffff',
        minHeight: '40vh',
        marginBottom: '100px',
        '@media (min-width: 768px) and (max-width: 1024px)': {
          minHeight: '100vh',
          height: 'auto',
          overflowY: 'auto',
          minHeight: '65vh',
        },
        '@media (min-width: 1024px) and (min-height: 1366px)': {
          height: 'auto',
          minHeight: '80vh',
        },
      }}>
        {/* Header Section */}
        <Box sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          marginBottom: '16px',
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Typography
            variant="h2"
            sx={{
              ml: 1,
            }}
          >
            Report : History Log Report {count > 0 ? `(${count})` : ""}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleDownloadClick}
              endIcon={<MoreVertIcon />}
              size={isMobile ? "small" : "medium"}
              disabled={!hasSearched || botData.length === 0}
                sx={{ textTransform: 'none',  fontSize: '15px' }}
            >
              Download
            </Button>

            <Menu
              anchorEl={downloadAnchorEl}
              open={Boolean(downloadAnchorEl)}
              onClose={handleDownloadClose}
            >
              <MenuItem onClick={handleDownloadExcel}>Excel</MenuItem>
              <MenuItem onClick={handleDownloadCSV}>CSV</MenuItem>
              <MenuItem onClick={handleDownloadPDF}>PDF</MenuItem>
            </Menu>
          </Box>
        </Box>

        <Box mb={3} p={2} sx={{ border: '1px solid #e0e0e0', borderRadius: '8px' }}>
          <Box sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: 2,
            alignItems: 'flex-end',
            marginBottom: '12px',
            flexWrap: 'wrap',
            backgroundColor: 'white',
            borderRadius: '8px',
            marginLeft: '8px',
          }}>
            {/* Fixed height containers for date fields */}
            <Box sx={{
              width: isMobile ? '100%' : '230px',
              minWidth: isMobile ? '100%' : '230px',
              height: '85px', // Fixed height
              display: 'flex',
              flexDirection: 'column',
            }}>
              <Typography variant="subtitle2" sx={{ 
                marginBottom: '4px', 
                fontFamily: '"Poppins", sans-serif',
                minHeight: '20px' // Fixed height for label
              }}>
                From Date <span style={{ color: 'red' }}>*</span>
              </Typography>
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'flex-start' }}>
                <DatePickerField
                  fieldName="fromDate"
                  formData={formData}
                  handleDateChange={handleDateChange}
                  handleBlur={handleBlur}
                  touchedFields={touchedFields}
                  fieldErrors={fieldErrors}
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    width: '100%',
                    '& .MuiInputBase-root': {
                      height: '55px'
                    }
                  }}
                />
              </Box>
            </Box>

            <Box sx={{
              width: isMobile ? '100%' : '230px',
              minWidth: isMobile ? '100%' : '230px',
              height: '85px', // Fixed height
              display: 'flex',
              flexDirection: 'column',
            }}>
              <Typography variant="subtitle2" sx={{ 
                marginBottom: '4px', 
                fontFamily: '"Poppins", sans-serif',
                minHeight: '20px' // Fixed height for label
              }}>
                To Date <span style={{ color: 'red' }}>*</span>
              </Typography>
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'flex-start' }}>
                <DatePickerField
                  fieldName="toDate"
                  formData={formData}
                  handleDateChange={handleDateChange}
                  handleBlur={handleBlur}
                  touchedFields={touchedFields}
                  fieldErrors={fieldErrors}
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    width: '100%',
                    '& .MuiInputBase-root': {
                      height: '55px'
                    }
                  }}
                />
              </Box>
            </Box>
            
          <Box sx={{
            width: isMobile ? '100%' : '200px',
            minWidth: isMobile ? '100%' : '200px',
            minHeight: '85px'
          }}>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px', fontFamily: '"Poppins", sans-serif' }}>
              Search Term
            </Typography>
            <TextField
              fullWidth
              size={isMobile ? "small" : "medium"}
              variant="outlined"
              placeholder="Enter search term"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{
                '& .MuiInputBase-root': {
                  height: '55px'
                }
              }}
            />
          </Box>

         
         <Box
          sx={{
            display: "flex",
            gap: 2,
            mb: "5px",
            width: isMobile ? "100%" : "auto",
            minHeight: "55px",
            alignItems: "center",
            "& button": {
              flex: isMobile ? 1 : "none",
              height: "55px",
              minHeight: "55px",
            },
          }}
        >
          <Button
          variant="contained"
          onClick={handleSearch}
          disabled={!((formData.fromDate && formData.toDate) || search.trim())}
          size={isMobile ? "small" : "medium"}
          sx={{
            bgcolor:
              (formData.fromDate && formData.toDate) || search.trim()
                ? "#FFAE00"
                : "#b0b0b0",
            "&:hover": {
              bgcolor:
                (formData.fromDate && formData.toDate) || search.trim()
                  ? "#E69C00"
                  : "#9e9e9e",
            },
            "&:focus": {
              outline: "2px solid #E69C00",
              outlineOffset: "2px",
            },
          }}
        >
          Search
        </Button>


            <Button
              onClick={resetSearch}
              size={isMobile ? "small" : "medium"}
              variant="outlined"
            >
              Reset
            </Button>
          </Box>
        </Box>
      </Box>

        
        {(fieldErrors.dateRange || fieldErrors.general) && (
          <Box sx={{ 
            mb: 2, 
            ml: 1,
            p: 1,
            backgroundColor: '#ffebee',
            border: '1px solid #f44336',
            borderRadius: 1
          }}>
            {fieldErrors.dateRange && (
              <Typography variant="body2" sx={{ color: '#f44336', fontSize: '12px' }}>
                {fieldErrors.dateRange}
              </Typography>
            )}
            {fieldErrors.general && (
              <Typography variant="body2" sx={{ color: '#f44336', fontSize: '12px' }}>
                {fieldErrors.general}
              </Typography>
            )}
          </Box>
        )}

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", padding: 2 }}>
            <Loader />
          </Box>
        ) : !hasSearched ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '200px',
              backgroundColor: '#f9f9f9',
              borderRadius: 2,
              border: '1px solid #e0e0e0',
              mt: 2
            }}
          >
            <NoDataFound discription={"Please use the search filters above to fetch and display updated Bot All Report data."} />
          </Box>
        ) : botData.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '200px',
              backgroundColor: '#f9f9f9',
              borderRadius: 2,
              border: '1px solid #e0e0e0',
              mt: 2
            }}
          >
            <NoDataFound discription={"No data found for the selected criteria."} />
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            sx={{
              maxHeight: isMobile ? '60vh' : 'none',
              overflow: 'auto',
              width: '100%',
              boxShadow: '0px 4px 15px rgba(0, 0, 0, 0.1)',
              borderRadius: '8px',
              mt: 2
            }}
          >
            <Table
              className={classes.table}
              sx={{
                '& .MuiTableCell-root': {
                  fontSize: isMobile ? '12px' : '14px',
                  padding: isMobile ? '8px' : '12px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: isMobile ? '100px' : '200px',
                  fontFamily: '"Poppins", sans-serif',
                }
              }}
              size={isMobile ? "small" : "medium"}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    className="styled-table-heading"
                    sx={{
                      fontSize: isMobile ? '12px' : '14px',
                      padding: isMobile ? '8px' : '12px',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleSort("carrierName")}
                  >
                    Carrier Name{" "}
                    {sortField === "carrierName" ? (sortOrder === "asc" ? "▲" : "▼") : ""}
                  </TableCell>
                  <TableCell
                    className="styled-table-heading"
                    sx={{
                      fontSize: isMobile ? '12px' : '14px',
                      padding: isMobile ? '8px' : '12px',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleSort("startDate")}
                  >
                    Start Date{" "}
                    {sortField === "startDate" ? (sortOrder === "asc" ? "▲" : "▼") : ""}
                  </TableCell>
                  <TableCell
                    className="styled-table-heading"
                    sx={{
                      fontSize: isMobile ? '12px' : '14px',
                      padding: isMobile ? '8px' : '12px',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleSort("status")}
                  >
                    Status{" "}
                    {sortField === "status" ? (sortOrder === "asc" ? "▲" : "▼") : ""}
                  </TableCell>
                  <TableCell
                    className="styled-table-heading"
                    sx={{
                      fontSize: isMobile ? '12px' : '14px',
                      padding: isMobile ? '8px' : '12px',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleSort("purchasedByUserEmail")}
                  >
                    Purchased By{" "}
                    {sortField === "purchasedByUserEmail" ? (sortOrder === "asc" ? "▲" : "▼") : ""}
                  </TableCell>
                  <TableCell
                    className="styled-table-heading"
                    sx={{
                      fontSize: isMobile ? '12px' : '14px',
                      padding: isMobile ? '8px' : '12px',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleSort("carrierAgencyCode")}
                  >
                    Agency Code{" "}
                    {sortField === "carrierAgencyCode" ? (sortOrder === "asc" ? "▲" : "▼") : ""}
                  </TableCell>
                  <TableCell
                    className="styled-table-heading"
                    sx={{
                      fontSize: isMobile ? '12px' : '14px',
                      padding: isMobile ? '8px' : '12px',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleSort("changes")}
                  >
                    Recent Updates{" "}
                    {sortField === "changes" ? (sortOrder === "asc" ? "▲" : "▼") : ""}
                  </TableCell>
                  <TableCell
                    className="styled-table-heading"
                    sx={{
                      fontSize: isMobile ? '12px' : '14px',
                      padding: isMobile ? '8px' : '12px',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleSort("insertedOn")}
                  >
                    Create Time{" "}
                    {sortField === "insertedOn" ? (sortOrder === "asc" ? "▲" : "▼") : ""}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {botData.map((row, index) => (
                  <TableRow
                    key={row.id || index}
                    className="styled-table-row"
                    sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}
                  >
                    <TableCell className={classes.tableCellData}>
                      {row.carrierName || ''}
                    </TableCell>
                    <TableCell className={classes.tableCellData}>
                      {row.startDate || ''}
                    </TableCell>
                    <TableCell className={classes.tableCellData}>
                      {row.status || ''}
                    </TableCell>
                    <TableCell className={classes.tableCellData}>
                      {row.purchasedByUserEmail || ''}
                    </TableCell>
                    <TableCell className={classes.tableCellData}>
                      {row.carrierAgencyCode || ''}
                    </TableCell>
                    <TableCell className={classes.tableCellData}>
                      {row.changes || ''}
                    </TableCell>
                    <TableCell className={classes.tableCellData}>
                      {row.insertedOn ? dayjs(row.insertedOn).format("MM-DD-YYYY, hh:mm:ss A") : ''}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {count > 0 && hasSearched && (
          <Box display="flex" justifyContent="flex-end" alignItems="center" p={2} sx={{
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 2 : 0
          }}>
            <Box display="flex" alignItems="center" sx={{
              marginRight: isMobile ? 0 : 2,
              marginBottom: isMobile ? 2 : 0
            }}>
              <Typography variant="body2" sx={{ marginRight: 1, fontFamily: '"Poppins", sans-serif' }}>
                Rows per page:
              </Typography>
              <FormControl variant="outlined" size="small" sx={{ minWidth: 70 }}>
                <Select
                  name="rowsPerPage"
                  value={rowsPerPage}
                  onChange={handleChangeRowsPerPage}
                  sx={{
                    fontSize: '14px',
                    height: 32,
                    fontFamily: '"Poppins", sans-serif'
                  }}
                >
                  {[5, 10, 15, 25, 50, 100].map((option) => (
                    <MenuItem key={option} value={option} sx={{ fontFamily: '"Poppins", sans-serif' }}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Pagination
              count={Math.ceil(count / rowsPerPage)}
              page={page + 1}
              onChange={(e, value) => {
                setPage(value - 1);
                dispatch(searchBotAllReportAction({
                  fromDate: formData.fromDate,
                  toDate: formData.toDate,
                  search,
                }, value - 1, rowsPerPage));
              }}
              showFirstButton
              showLastButton
              sx={{
                '& .MuiPaginationItem-root': {
                  fontFamily: '"Poppins", sans-serif'
                }
              }}
            />
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
}

export default BotAllReport;

const getBotData = (bots) => {
  if (!Array.isArray(bots)) {
    return [];
  }
  return bots;
};